import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { Row } from "../types";
import {
  AbaAnexos,
  AbaChecklist,
  AbaEquipe,
  AbaLinks,
  AbaOrcamento,
  AbaParticipantes,
  AbaProgramacao,
  type Anexo,
  type Atividade,
  type Despesa,
  type ItemChecklist,
  type LinkEvento,
  type Membro,
  type Participante,
} from "./EventoAbas";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome } from "../ordem";
import { Progresso, dataBr, moeda, periodoTexto, tomDoStatus } from "./EventoPecas";
import { IconClose, IconCheck } from "../icons";

/**
 * CENTRAL DO EVENTO — o evento tratado como um projeto, não como uma data.
 *
 * Um cabeçalho com o que importa de relance (capa, período, local, situação,
 * responsável e os números) e abas para o resto. Cada aba edita a sua lista em
 * memória e o botão "Salvar" grava só o que mudou — é o mesmo modelo do plano
 * de ação do RH: ninguém para no meio de organizar um evento para salvar item
 * por item.
 *
 * O que NÃO muda aqui: o álbum de fotos e o cadastro básico (nome, data, local,
 * descrição) continuam com as telas que já existiam e funcionam.
 */

const TIPOS = [
  "Evento interno",
  "Evento externo",
  "Campanha",
  "Workshop",
  "Treinamento",
  "Palestra",
  "Confraternização",
  "Atividade esportiva",
  "Ação social",
  "Comemoração",
  "Outro",
];
const STATUS = ["Planejamento", "Confirmado", "Em andamento", "Concluído", "Cancelado"];
const CANAIS = ["WhatsApp", "E-mail", "Instagram", "Intranet", "Comunicação interna", "Outros"];

type AbaKey =
  | "geral"
  | "programacao"
  | "equipe"
  | "participantes"
  | "checklist"
  | "orcamento"
  | "comunicacao"
  | "arquivos"
  | "links";

const ABAS: { key: AbaKey; label: string }[] = [
  { key: "geral", label: "Visão geral" },
  { key: "programacao", label: "Programação" },
  { key: "participantes", label: "Participantes" },
  { key: "checklist", label: "Checklist" },
  { key: "equipe", label: "Equipe" },
  { key: "orcamento", label: "Orçamento" },
  { key: "arquivos", label: "Arquivos" },
  { key: "comunicacao", label: "Comunicação" },
  { key: "links", label: "Links" },
];

export default function EventoCentral({
  eventoId,
  colaboradores,
  usuario,
  onClose,
  onMudou,
}: {
  eventoId: number;
  colaboradores: Row[];
  usuario?: string;
  onClose: () => void;
  onMudou: (mensagem: string) => void;
}) {
  const [aba, setAba] = useState<AbaKey>("geral");
  const [dados, setDados] = useState<any>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  // A ficha e as listas em memória; o botão Salvar grava.
  const [ficha, setFicha] = useState<Record<string, any>>({});
  const [tags, setTags] = useState<string[]>([]);
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [equipe, setEquipe] = useState<Membro[]>([]);
  const [participantes, setParticipantes] = useState<Participante[]>([]);
  const [checklist, setChecklist] = useState<ItemChecklist[]>([]);
  const [despesas, setDespesas] = useState<Despesa[]>([]);
  const [links, setLinks] = useState<LinkEvento[]>([]);
  /** Anexos: sobem na hora (arquivo não espera o "Salvar evento"). */
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [enviandoAnexo, setEnviandoAnexo] = useState(false);
  const podeExcluir = usePodeExcluir();

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const d = await api.endomarketing.evento.central(eventoId);
      setDados(d);
      const e = d.evento ?? {};
      setFicha({
        titulo_interno: e.titulo_interno ?? "",
        tipo: e.tipo ?? "Evento interno",
        status: e.status ?? "Planejamento",
        data: e.data ?? "",
        data_fim: e.data_fim ?? "",
        hora_inicio: e.hora_inicio ?? "",
        hora_fim: e.hora_fim ?? "",
        dia_inteiro: e.dia_inteiro === true,
        endereco: e.endereco ?? "",
        cidade: e.cidade ?? "",
        estado: e.estado ?? "",
        mapa_url: e.mapa_url ?? "",
        observacoes: e.observacoes ?? "",
        responsavel_id: e.responsavel_id ?? "",
        responsavel_nome: e.responsavel_nome ?? "",
        com_mensagem: e.com_mensagem ?? "",
        com_publico: e.com_publico ?? "",
        com_canais: e.com_canais ?? "",
        com_inicio: e.com_inicio ?? "",
        com_fim: e.com_fim ?? "",
        com_link: e.com_link ?? "",
        com_observacoes: e.com_observacoes ?? "",
        recorrencia: e.recorrencia ?? "Não se repete",
        recorrencia_ate: e.recorrencia_ate ?? "",
      });
      setTags(d.tags ?? []);
      setAtividades(
        (d.atividades ?? []).map((a: any) => ({
          id: a.id,
          dia: a.dia ?? "",
          hora_inicio: a.hora_inicio ?? "",
          hora_fim: a.hora_fim ?? "",
          titulo: a.titulo ?? "",
          descricao: a.descricao ?? "",
          responsavel: a.responsavel ?? "",
          local: a.local ?? "",
          observacoes: a.observacoes ?? "",
          ordem: a.ordem,
        })),
      );
      setEquipe(
        (d.equipe ?? []).map((m: any) => ({
          id: m.id,
          colaborador_id: m.colaborador_id,
          nome: m.nome ?? "",
          funcao: m.funcao ?? "",
          contato: m.contato ?? "",
          observacao: m.observacao ?? "",
        })),
      );
      setParticipantes(
        (d.participantes ?? []).map((p: any) => ({
          id: p.id,
          colaborador_id: p.colaborador_id,
          nome: p.nome ?? "",
          situacao: p.situacao ?? "Pendente",
          presente: p.presente === true,
          observacao: p.observacao ?? "",
        })),
      );
      setChecklist(
        (d.checklist ?? []).map((c: any) => ({
          id: c.id,
          titulo: c.titulo ?? "",
          responsavel: c.responsavel ?? "",
          prazo: c.prazo ?? "",
          prioridade: c.prioridade ?? "Média",
          concluido: c.concluido === true,
          concluido_em: c.concluido_em,
          concluido_por: c.concluido_por,
        })),
      );
      setDespesas(
        (d.despesas ?? []).map((x: any) => ({
          id: x.id,
          descricao: x.descricao ?? "",
          categoria: x.categoria ?? "",
          previsto: x.previsto == null ? "" : String(x.previsto).replace(".", ","),
          realizado: x.realizado == null ? "" : String(x.realizado).replace(".", ","),
          status: x.status ?? "Previsto",
          observacao: x.observacao ?? "",
        })),
      );
      setLinks(
        (d.links ?? []).map((l: any) => ({
          id: l.id,
          nome: l.nome ?? "",
          url: l.url ?? "",
          descricao: l.descricao ?? "",
        })),
      );
      // Os anexos vêm por fora da central: são arquivos em disco, não uma
      // lista editável, e a listagem confere item a item se o arquivo existe.
      setAnexos(await api.endomarketing.evento.anexos.listar(eventoId).catch(() => []));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível abrir o evento.");
    } finally {
      setCarregando(false);
    }
  }, [eventoId]);

  async function enviarAnexos(files: FileList) {
    setEnviandoAnexo(true);
    setErro(null);
    try {
      const r = await api.endomarketing.evento.anexos.enviar(eventoId, files);
      setAnexos(await api.endomarketing.evento.anexos.listar(eventoId));
      setAviso(`${r.salvos} arquivo(s) anexado(s).`);
      setTimeout(() => setAviso(null), 2500);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível anexar.");
    } finally {
      setEnviandoAnexo(false);
    }
  }

  async function removerAnexo(a: Anexo) {
    if (!confirm(`Excluir "${a.nome}"?`)) return;
    try {
      await api.endomarketing.evento.anexos.remover(eventoId, a.arquivo);
      setAnexos(await api.endomarketing.evento.anexos.listar(eventoId));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível excluir.");
    }
  }

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * Salva TUDO o que está na tela.
   *
   * A ficha vai numa chamada e cada lista na sua — o servidor grava lista por
   * lista dentro de uma transação, então uma falha no orçamento não leva a
   * programação junto.
   */
  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      await api.endomarketing.evento.detalhes(eventoId, { ...ficha, tags });
      await Promise.all([
        api.endomarketing.evento.lista(eventoId, "atividades", atividades as any),
        api.endomarketing.evento.lista(eventoId, "equipe", equipe as any),
        api.endomarketing.evento.lista(eventoId, "participantes", participantes as any),
        api.endomarketing.evento.lista(eventoId, "checklist", checklist as any),
        api.endomarketing.evento.lista(eventoId, "despesas", despesas as any),
        api.endomarketing.evento.lista(eventoId, "links", links as any),
      ]);
      await carregar();
      setAviso("Evento salvo.");
      onMudou("Evento atualizado.");
      setTimeout(() => setAviso(null), 2500);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível salvar.");
    } finally {
      setSalvando(false);
    }
  }

  /**
   * Gera as datas da repetição. Salva a ficha antes: a recorrência escolhida
   * agora só existe na tela, e o servidor precisa dela para calcular as datas.
   */
  async function gerarSerie() {
    setSalvando(true);
    setErro(null);
    try {
      await api.endomarketing.evento.detalhes(eventoId, {
        data: ficha.data,
        recorrencia: ficha.recorrencia,
        recorrencia_ate: ficha.recorrencia_ate,
      });
      const r = await api.endomarketing.evento.gerarOcorrencias(eventoId);
      await carregar();
      setAviso(r.mensagem);
      onMudou(r.mensagem);
      setTimeout(() => setAviso(null), 4000);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível gerar as datas.");
    } finally {
      setSalvando(false);
    }
  }

  async function adicionarEmLote(ids: number[]) {
    try {
      const r = await api.endomarketing.evento.participantesEmLote(eventoId, ids);
      await carregar();
      setAviso(r.mensagem);
      setTimeout(() => setAviso(null), 3000);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível adicionar.");
    }
  }

  const evento = dados?.evento ?? {};
  const resumo = dados?.resumo ?? {};
  const capaUrl = evento.capa
    ? `/api/endomarketing/eventos/${eventoId}/fotos/${encodeURIComponent(evento.capa)}`
    : null;

  function mudarFicha(campo: string, valor: unknown) {
    setFicha((f) => ({ ...f, [campo]: valor }));
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card ev-central" onMouseDown={(e) => e.stopPropagation()}>
        {/* ---------- Cabeçalho: o evento de relance ---------- */}
        <div className="ev-capa" style={capaUrl ? { backgroundImage: `url(${capaUrl})` } : undefined}>
          <button type="button" className="icon-btn ev-fechar" onClick={onClose}>
            <IconClose size={18} />
          </button>
          <div className="ev-capa-texto">
            <span className={`pill ${tomDoStatus(ficha.status)}`}>{ficha.status || "Planejamento"}</span>
            <h3>{evento.nome ?? "Evento"}</h3>
            <p>
              {periodoTexto({
                data: ficha.data,
                data_fim: ficha.data_fim,
                hora_inicio: ficha.hora_inicio,
                hora_fim: ficha.hora_fim,
                dia_inteiro: ficha.dia_inteiro,
              })}
            </p>
            <p className="ev-capa-local">
              {[evento.local, ficha.cidade, ficha.estado].filter(Boolean).join(" · ")}
              {ficha.responsavel_nome ? ` · resp. ${ficha.responsavel_nome}` : ""}
            </p>
          </div>
        </div>

        {/* ---------- Números ---------- */}
        <div className="ev-numeros">
          <span>
            <strong>{resumo.atividades ?? 0}</strong> atividade(s)
          </span>
          <span>
            <strong>{resumo.participantes ?? 0}</strong> participante(s)
            {resumo.confirmados ? <small className="muted"> · {resumo.confirmados} confirmado(s)</small> : null}
          </span>
          <span>
            <strong>
              {resumo.checklist_feitas ?? 0}/{resumo.checklist_total ?? 0}
            </strong>{" "}
            no checklist
          </span>
          <span>
            <strong>{moeda(resumo.previsto ?? 0)}</strong> previsto
          </span>
        </div>

        {/* ---------- Abas ---------- */}
        <div className="ev-abas">
          {ABAS.map((a) => (
            <button
              key={a.key}
              type="button"
              className={aba === a.key ? "ativa" : ""}
              onClick={() => setAba(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="modal-body ev-corpo">
          {carregando ? (
            <div className="empty">Abrindo o evento…</div>
          ) : (
            <>
              {aviso && (
                <div className="form-ok">
                  <IconCheck size={15} /> {aviso}
                </div>
              )}

              {aba === "geral" && (
                <div className="ev-aba">
                  <div className="form-grid">
                    <label className="field">
                      <span>Título interno</span>
                      <input
                        value={ficha.titulo_interno}
                        onChange={(e) => mudarFicha("titulo_interno", e.target.value)}
                        placeholder="Como a equipe chama o evento"
                      />
                    </label>
                    <label className="field">
                      <span>Tipo</span>
                      <select value={ficha.tipo} onChange={(e) => mudarFicha("tipo", e.target.value)}>
                        {TIPOS.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Situação</span>
                      <select value={ficha.status} onChange={(e) => mudarFicha("status", e.target.value)}>
                        {STATUS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="field">
                      <span>Responsável geral</span>
                      <select
                        value={ficha.responsavel_id || ""}
                        onChange={(e) => {
                          const c = colaboradores.find((x) => String(x.id) === e.target.value);
                          mudarFicha("responsavel_id", e.target.value);
                          mudarFicha("responsavel_nome", c ? String(c.nome) : "");
                        }}
                      >
                        <option value="">— escolher —</option>
                        {ordenarPorNome(colaboradores, (c) => c.nome).map((c) => (
                          <option key={String(c.id)} value={String(c.id)}>
                            {String(c.nome)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="field">
                      <span>Início</span>
                      <input type="date" value={ficha.data} onChange={(e) => mudarFicha("data", e.target.value)} />
                    </label>
                    <label className="field">
                      <span>Término</span>
                      <input
                        type="date"
                        value={ficha.data_fim}
                        min={ficha.data || undefined}
                        onChange={(e) => mudarFicha("data_fim", e.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span>Das</span>
                      <input
                        type="time"
                        value={ficha.hora_inicio}
                        disabled={ficha.dia_inteiro}
                        onChange={(e) => mudarFicha("hora_inicio", e.target.value)}
                      />
                    </label>
                    <label className="field">
                      <span>Até</span>
                      <input
                        type="time"
                        value={ficha.hora_fim}
                        disabled={ficha.dia_inteiro}
                        onChange={(e) => mudarFicha("hora_fim", e.target.value)}
                      />
                    </label>
                    <label className="field ev-check-inline">
                      <input
                        type="checkbox"
                        checked={ficha.dia_inteiro}
                        onChange={(e) => mudarFicha("dia_inteiro", e.target.checked)}
                      />
                      <span>Evento de dia inteiro</span>
                    </label>

                    <label className="field field-wide">
                      <span>Endereço</span>
                      <input value={ficha.endereco} onChange={(e) => mudarFicha("endereco", e.target.value)} />
                    </label>
                    <label className="field">
                      <span>Cidade</span>
                      <input value={ficha.cidade} onChange={(e) => mudarFicha("cidade", e.target.value)} />
                    </label>
                    <label className="field">
                      <span>Estado</span>
                      <input
                        value={ficha.estado}
                        maxLength={2}
                        onChange={(e) => mudarFicha("estado", e.target.value.toUpperCase())}
                        placeholder="SP"
                      />
                    </label>
                    <label className="field field-wide">
                      <span>Link do mapa</span>
                      <input
                        value={ficha.mapa_url}
                        onChange={(e) => mudarFicha("mapa_url", e.target.value)}
                        placeholder="https://maps.google.com/..."
                      />
                    </label>
                    <label className="field field-wide">
                      <span>Observações internas</span>
                      <textarea
                        rows={2}
                        value={ficha.observacoes}
                        onChange={(e) => mudarFicha("observacoes", e.target.value)}
                        placeholder="O que a equipe precisa saber e não vai na divulgação"
                      />
                    </label>
                  </div>

                  {/* TAGS */}
                  <div className="ev-tags-edit">
                    <span className="muted">Tags</span>
                    <div className="ev-tags">
                      {tags.map((t) => (
                        <span key={t} className="ev-tag">
                          #{t}
                          <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))}>
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        className="ev-tag-input"
                        placeholder="+ tag e Enter"
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") return;
                          e.preventDefault();
                          const v = e.currentTarget.value.trim().replace(/^#/, "");
                          if (v && !tags.includes(v)) setTags([...tags, v]);
                          e.currentTarget.value = "";
                        }}
                      />
                    </div>
                  </div>

                  {/* REPETIÇÃO: a série é gerada por um clique, nunca sozinha —
                      criar 50 eventos sem a pessoa pedir seria assustador. */}
                  <div className="ev-recorrencia">
                    <label className="field">
                      <span>Repetir</span>
                      <select
                        value={ficha.recorrencia ?? "Não se repete"}
                        onChange={(e) => mudarFicha("recorrencia", e.target.value)}
                      >
                        {["Não se repete", "Diariamente", "Semanalmente", "Mensalmente", "Anualmente"].map(
                          (r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                    {(ficha.recorrencia ?? "Não se repete") !== "Não se repete" && (
                      <>
                        <label className="field">
                          <span>Até</span>
                          <input
                            type="date"
                            value={ficha.recorrencia_ate ?? ""}
                            min={ficha.data || undefined}
                            onChange={(e) => mudarFicha("recorrencia_ate", e.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={salvando || !ficha.recorrencia_ate}
                          onClick={gerarSerie}
                          title="Cria os eventos da repetição (a programação e o checklist vão junto)"
                        >
                          Gerar as datas
                        </button>
                        <small className="muted">
                          Cada data vira um evento próprio, com a programação e o checklist copiados.
                          Gerar de novo não duplica o que já existe.
                        </small>
                      </>
                    )}
                  </div>

                  {/* Resumo do que está pronto */}
                  {(resumo.checklist_total ?? 0) > 0 && (
                    <div className="ev-progresso">
                      <Progresso pct={resumo.checklist_pct ?? 0} />
                      <span className="muted">
                        {resumo.checklist_feitas} de {resumo.checklist_total} tarefas ({resumo.checklist_pct}%)
                      </span>
                    </div>
                  )}

                  {(dados?.historico ?? []).length > 0 && (
                    <p className="ev-ultima">
                      Última atualização por <strong>{dados.historico[0].usuario ?? "—"}</strong> em{" "}
                      {new Date(dados.historico[0].criado_em).toLocaleString("pt-BR", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                      {dados.historico[0].detalhe ? ` — ${dados.historico[0].detalhe}` : ""}
                    </p>
                  )}
                </div>
              )}

              {aba === "programacao" && (
                <AbaProgramacao itens={atividades} onChange={setAtividades} diaPadrao={ficha.data} />
              )}
              {aba === "participantes" && (
                <AbaParticipantes
                  itens={participantes}
                  onChange={setParticipantes}
                  colaboradores={colaboradores}
                  onAdicionarEmLote={adicionarEmLote}
                />
              )}
              {aba === "checklist" && (
                <AbaChecklist itens={checklist} onChange={setChecklist} usuario={usuario} />
              )}
              {aba === "equipe" && (
                <AbaEquipe itens={equipe} onChange={setEquipe} colaboradores={colaboradores} />
              )}
              {aba === "orcamento" && <AbaOrcamento itens={despesas} onChange={setDespesas} />}
              {aba === "links" && <AbaLinks itens={links} onChange={setLinks} />}
              {aba === "arquivos" && (
                <AbaAnexos
                  itens={anexos}
                  enviando={enviandoAnexo}
                  podeExcluir={podeExcluir}
                  onEnviar={enviarAnexos}
                  onRemover={removerAnexo}
                />
              )}

              {aba === "comunicacao" && (
                <div className="ev-aba">
                  <div className="form-grid">
                    <label className="field field-wide">
                      <span>Mensagem de divulgação</span>
                      <textarea
                        rows={3}
                        value={ficha.com_mensagem}
                        onChange={(e) => mudarFicha("com_mensagem", e.target.value)}
                        placeholder="O texto que vai no comunicado"
                      />
                    </label>
                    <label className="field">
                      <span>Público-alvo</span>
                      <input
                        value={ficha.com_publico}
                        onChange={(e) => mudarFicha("com_publico", e.target.value)}
                        placeholder="Ex.: todos os colaboradores"
                      />
                    </label>
                    <label className="field">
                      <span>Canais</span>
                      <input
                        value={ficha.com_canais}
                        onChange={(e) => mudarFicha("com_canais", e.target.value)}
                        placeholder={CANAIS.join(", ")}
                        list="ev-canais"
                      />
                      <datalist id="ev-canais">
                        {CANAIS.map((c) => (
                          <option key={c} value={c} />
                        ))}
                      </datalist>
                    </label>
                    <label className="field">
                      <span>Divulgar de</span>
                      <input type="date" value={ficha.com_inicio} onChange={(e) => mudarFicha("com_inicio", e.target.value)} />
                    </label>
                    <label className="field">
                      <span>até</span>
                      <input type="date" value={ficha.com_fim} onChange={(e) => mudarFicha("com_fim", e.target.value)} />
                    </label>
                    <label className="field field-wide">
                      <span>Link de inscrição</span>
                      <input
                        value={ficha.com_link}
                        onChange={(e) => mudarFicha("com_link", e.target.value)}
                        placeholder="https://..."
                      />
                    </label>
                    <label className="field field-wide">
                      <span>Observações</span>
                      <textarea
                        rows={2}
                        value={ficha.com_observacoes}
                        onChange={(e) => mudarFicha("com_observacoes", e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              )}

              {erro && <div className="form-error">{erro}</div>}
            </>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={salvando}>
            Fechar
          </button>
          <button type="button" className="btn-primary" onClick={salvar} disabled={salvando || carregando}>
            {salvando ? "Salvando…" : "Salvar evento"}
          </button>
        </div>
      </div>
    </div>
  );
}
